import { useMemo } from 'react';
import useStores from '@stores/useStores';
import { useActionGuard, ActionType, ResourceType } from '@hooks/useActionGuard';
import { constructPermissionResult } from '@hooks/useActionGuard/utils';
import { ApiAccount } from '@api';

import { AccountResourceType } from '../../store';

/**
 * Custom hook that computes edit permissions for a list of accounts.
 * This hook pre-computes permissions at the component level to avoid calling hooks
 * inside loops, which violates React's Rules of Hooks.
 *
 * Data-retention accounts are gated on org-admin (matching the data-management
 * page); source/destination behavior is preserved.
 *
 * @param accounts - Array of accounts to check permissions for
 * @returns A map of account IDs to their edit permission results
 */
export const useEditAccountPermissions = (
  accounts: ApiAccount[],
  resourceType: AccountResourceType,
) => {
  const { userStore, permissionsStore } = useStores();
  const { canPerformAction, isNewPermissionModelEnforcementEnabled } = useActionGuard();

  const resourceLabel = resourceType === 'dataRetention' ? 'data retention' : resourceType;

  const dataRetentionAllowed = isNewPermissionModelEnforcementEnabled
    ? userStore.isOrgAdmin
    : permissionsStore.hasPermission({ resourceType: 'workspace', action: 'edit' });

  const sourceOrDestinationAllowed = canPerformAction({
    new: {
      actionType: ActionType.CREATE,
      resource: {
        type: resourceType === 'destination' ? ResourceType.DESTINATION : ResourceType.EVENT_STREAM,
      },
    },
    old: { resourceType: 'workspace', action: 'edit' },
  }).isAllowed;

  const isAllowed =
    resourceType === 'dataRetention' ? dataRetentionAllowed : sourceOrDestinationAllowed;

  const editAccountPermission = constructPermissionResult(
    isAllowed,
    `You do not have permission to edit ${resourceLabel} accounts.`,
  );

  const editPermissionsMap = useMemo(
    () => Object.fromEntries(accounts.map((account) => [account.id, editAccountPermission])),
    [accounts],
  );

  return { editPermissionsMap };
};
